import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LogIn, RefreshCw, ChevronLeft, TrendingUp, Users, Skull, Crown, Coins, Share2 } from "lucide-react";

function Stat({ label, value, hint, testid }) {
  return (
    <div className="panel p-5" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] ash-text">{label}</div>
      <div className="mt-1 font-heading text-3xl text-[#f0e6d2]">{value}</div>
      {hint && <div className="mt-1 text-[10px] ash-text tracking-widest">{hint}</div>}
    </div>
  );
}

function Table({ headers, rows, testid, empty = "No data yet." }) {
  return (
    <div className="panel overflow-hidden" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] tracking-[0.3em] ash-text">
            {headers.map((h) => (
              <th key={h} className="text-left px-4 py-3 border-b border-white/10">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={headers.length} className="px-4 py-6 text-center ash-text italic">{empty}</td></tr>
          )}
          {rows.map((cells, i) => (
            <tr key={i} className="border-b border-white/5 last:border-0">
              {cells.map((c, j) => (
                <td key={j} className="px-4 py-3 font-narrative text-[#f0e6d2]">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminAttributionPage() {
  const [auth, setAuth] = useState(null); // {is_admin: bool, error?: string}
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [d, r] = await Promise.all([
        api.get("/admin/attribution"),
        api.get("/admin/revenue").catch(() => null),
      ]);
      setData(d);
      if (r) setLedger(r);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setAuth({ is_admin: false, error: "not_signed_in" });
      else if (status === 403) setAuth({ is_admin: false, error: "not_admin" });
      else setAuth({ is_admin: false, error: "load_failed" });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await api.get("/admin/me");
        setAuth({ is_admin: true });
        load();
      } catch (e) {
        if (e?.response?.status === 401) setAuth({ is_admin: false, error: "not_signed_in" });
        else setAuth({ is_admin: false, error: "not_admin" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (auth === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-heading text-2xl ash-text">Verifying credentials...</div>
      </div>
    );
  }

  if (!auth.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-gate">
        <div className="panel p-10 max-w-lg text-center">
          <div className="text-[10px] tracking-[0.4em] blood-text">RESTRICTED</div>
          <h1 className="font-heading text-4xl mt-3 text-[#f0e6d2] italic">The Keeper's Ledger</h1>
          <p className="ash-text mt-3 text-sm italic">
            {auth.error === "not_signed_in"
              ? "Sign in with an authorized administrator account to view attribution."
              : auth.error === "not_admin"
              ? "Your account does not have keeper's access."
              : "Could not fetch the ledger."}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            {auth.error === "not_signed_in" && (
              <button onClick={goSignIn} className="btn-primary flex items-center gap-2" data-testid="admin-signin-btn">
                <LogIn size={14} /> SIGN IN
              </button>
            )}
            <Link to="/" className="btn-glow" data-testid="admin-back-btn">RETURN TO REALM</Link>
          </div>
        </div>
      </div>
    );
  }

  const t = data?.totals || {};
  const paidVsRuns = t.runs_finished > 0 ? ((t.payments_completed / t.runs_finished) * 100).toFixed(1) : "0.0";
  const winRate = t.runs_finished > 0 ? ((t.victories / t.runs_finished) * 100).toFixed(1) : "0.0";

  return (
    <div className="min-h-screen px-4 md:px-8 lg:px-12 py-6" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="ash-text hover:text-white flex items-center gap-1 text-xs tracking-widest">
            <ChevronLeft size={14} /> DASHBOARD
          </Link>
          <div className="h-4 w-px bg-white/20" />
          <div>
            <div className="text-[10px] tracking-[0.4em] gold-text">THE KEEPER'S LEDGER</div>
            <div className="font-heading text-2xl text-[#f0e6d2] leading-none mt-1">Attribution & Analytics</div>
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="panel px-3 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          <span className="text-[10px] tracking-[0.3em] ash-text">{refreshing ? "READING RUNES" : "REFRESH"}</span>
        </button>
      </div>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="admin-tabs">
        <span className="panel px-3 py-1.5 border-[#d4af37]/50 text-[10px] tracking-[0.3em] gold-text">
          ATTRIBUTION
        </span>
        <Link
          to="/admin/monetization"
          className="panel px-3 py-1.5 flex items-center gap-1.5 hover:border-[#d4af37] transition-colors text-[10px] tracking-[0.3em] ash-text"
          data-testid="admin-tab-monetization"
        >
          THE VAULT · MRR &amp; ADS →
        </Link>
      </div>

      {!data ? (
        <div className="panel p-10 text-center ash-text italic">Consulting the runes...</div>
      ) : (
        <>
          {/* TODAY'S LEDGER — daily revenue snapshot */}
          {ledger && <TodaysLedger ledger={ledger} />}

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            <Stat testid="kpi-users"      label="USERS"          value={t.users} hint={<><Users size={10} className="inline mr-1"/>seeded</>} />
            <Stat testid="kpi-runs"       label="RUNS FINISHED"  value={t.runs_finished} hint={`${t.active_runs} active`} />
            <Stat testid="kpi-deaths"     label="DEATHS"         value={t.deaths} hint={<><Skull size={10} className="inline mr-1"/>permadeath</>} />
            <Stat testid="kpi-victories"  label="VICTORIES"      value={t.victories} hint={<><Crown size={10} className="inline mr-1"/>{winRate}% win rate</>} />
            <Stat testid="kpi-cards"      label="CARDS CREATED"  value={t.cards_created} hint={<><Share2 size={10} className="inline mr-1"/>shareable</>} />
            <Stat testid="kpi-revenue"    label="REVENUE (USD)"  value={`$${(t.revenue_usd || 0).toFixed(2)}`} hint={<><Coins size={10} className="inline mr-1"/>{t.payments_completed} payments</>} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* UTM sources */}
            <div>
              <div className="text-[10px] tracking-[0.4em] gold-text mb-2 flex items-center gap-1">
                <TrendingUp size={12} /> BY UTM SOURCE
              </div>
              <Table
                testid="table-utm-source"
                headers={["Source", "Runs", "Deaths", "Victories"]}
                rows={(data.by_utm_source || []).map((r) => [r.source, r.runs, r.deaths, r.victories])}
                empty="No attributed runs yet. Share a card to seed the funnel."
              />
            </div>

            {/* UTM campaigns */}
            <div>
              <div className="text-[10px] tracking-[0.4em] gold-text mb-2 flex items-center gap-1">
                <TrendingUp size={12} /> BY UTM CAMPAIGN
              </div>
              <Table
                testid="table-utm-campaign"
                headers={["Campaign", "Runs", "Deaths", "Wins", "Avg Floor", "Max"]}
                rows={(data.by_utm_campaign || []).map((r) => [r.campaign, r.runs, r.deaths, r.victories, r.avg_floors, r.max_floors])}
                empty="No campaigns attributed yet."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Class distribution */}
            <div>
              <div className="text-[10px] tracking-[0.4em] gold-text mb-2">CLASS DISTRIBUTION</div>
              <Table
                testid="table-class"
                headers={["Class", "Runs", "Avg Floor"]}
                rows={(data.by_class || []).map((r) => [r.class_key, r.runs, r.avg_floors])}
              />
            </div>

            {/* Payments by kind */}
            <div>
              <div className="text-[10px] tracking-[0.4em] gold-text mb-2">PAYMENTS BY KIND</div>
              <Table
                testid="table-payments"
                headers={["Kind", "Count", "Revenue ($)"]}
                rows={(data.by_payment_kind || []).map((r) => [r.kind, r.count, r.revenue.toFixed(2)])}
                empty="No completed payments yet."
              />
            </div>
          </div>

          {/* Top cards */}
          <div>
            <div className="text-[10px] tracking-[0.4em] gold-text mb-2">TOP RUNS (BY FLOOR)</div>
            <Table
              testid="table-top-cards"
              headers={["User", "Class", "Floor", "Level", "Outcome", "Campaign", "Card"]}
              rows={(data.top_cards || []).map((r) => [
                r.user_name || "Adventurer",
                r.class_name || "?",
                r.floors_cleared,
                r.final_level,
                r.outcome === "victory" ? <span className="gold-text">Victory</span> : <span className="blood-text">Death</span>,
                r.utm_campaign || <span className="ash-text italic">direct</span>,
                <Link to={`/card/${r.share_id}`} className="gold-text underline text-xs">/card/{r.share_id}</Link>,
              ])}
              empty="No runs recorded yet."
            />
          </div>

          <div className="mt-8 text-[10px] tracking-widest ash-text text-right">
            Generated {new Date(data.generated_at).toLocaleString()} · Conversion {paidVsRuns}%
          </div>
        </>
      )}
    </div>
  );
}


/**
 * Compact daily revenue snapshot for the admin morning check-in.
 * Renders today vs yesterday delta, 7-day total, top pack, hourly UTC bars, and recent txns.
 */
function TodaysLedger({ ledger }) {
  const today = ledger.today || { revenue: 0, count: 0 };
  const y     = ledger.yesterday || { revenue: 0, count: 0 };
  const week  = ledger.seven_day || { revenue: 0, count: 0 };
  const at    = ledger.all_time || { revenue: 0, count: 0 };
  const hourly = ledger.hourly || [];
  const peak = Math.max(...hourly, 0.01);
  const delta = today.revenue - y.revenue;
  const deltaPct = y.revenue > 0 ? ((delta / y.revenue) * 100).toFixed(0) : (today.revenue > 0 ? "+∞" : "0");
  const deltaColor = delta > 0 ? "#7cffa0" : delta < 0 ? "#ff6b3a" : "#a89373";

  return (
    <div
      className="panel p-6 mb-6 relative overflow-hidden"
      data-testid="todays-ledger"
      style={{
        background: "radial-gradient(ellipse at top left, rgba(90,60,10,0.25), rgba(10,5,3,0.95))",
        borderColor: "rgba(212,175,55,0.5)",
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] tracking-[0.45em] gold-text">TODAY&apos;S LEDGER · UTC</div>
          <div className="font-heading text-2xl text-[#f0e6d2] leading-none mt-1">
            {ledger.day_bucket || new Date().toISOString().slice(0, 10)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.3em] ash-text">TOP PACK TODAY</div>
          <div className="font-heading text-lg gold-text mt-1" data-testid="ledger-top-pack">
            {ledger.today_top_pack
              ? `${ledger.today_top_pack.product_key} · ×${ledger.today_top_pack.count}`
              : <span className="ash-text italic text-sm">no sales yet</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <LedgerCell testid="ledger-today"   label="TODAY"       primary={`$${today.revenue.toFixed(2)}`} sub={`${today.count} txn`} />
        <LedgerCell
          testid="ledger-delta"
          label="VS YESTERDAY"
          primary={`${delta >= 0 ? "+" : ""}$${Math.abs(delta).toFixed(2)}`}
          sub={<span style={{ color: deltaColor }}>{deltaPct}%</span>}
          primaryColor={deltaColor}
        />
        <LedgerCell testid="ledger-week"    label="7-DAY"       primary={`$${week.revenue.toFixed(2)}`} sub={`${week.count} txn`} />
        <LedgerCell testid="ledger-all"     label="ALL-TIME"    primary={`$${at.revenue.toFixed(2)}`} sub={`${at.count} txn`} />
      </div>

      {/* Hourly UTC bars */}
      <div>
        <div className="text-[10px] tracking-[0.3em] ash-text mb-2">HOURLY · UTC</div>
        <div className="flex items-end gap-[2px] h-16" data-testid="ledger-hourly-bars">
          {hourly.map((v, h) => (
            <div
              key={h}
              title={`${h.toString().padStart(2, "0")}:00 · $${v.toFixed(2)}`}
              className="flex-1 relative transition-all"
              style={{
                height: `${Math.max(3, (v / peak) * 100)}%`,
                background: v > 0
                  ? "linear-gradient(180deg, #d4af37, rgba(226,88,34,0.6))"
                  : "rgba(212,175,55,0.08)",
                boxShadow: v > 0 ? "0 0 6px rgba(212,175,55,0.4)" : "none",
              }}
            />
          ))}
        </div>
        <div className="flex justify-between text-[8px] tracking-widest ash-text mt-1">
          <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
        </div>
      </div>

      {/* Recent transactions strip */}
      {ledger.recent?.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/10">
          <div className="text-[10px] tracking-[0.3em] ash-text mb-2">RECENT SOULS TRADED</div>
          <div className="flex flex-wrap gap-2" data-testid="ledger-recent">
            {ledger.recent.map((r, i) => (
              <div
                key={i}
                className="text-[10px] tracking-widest px-2 py-1 border border-[#d4af37]/30"
                style={{ background: "rgba(0,0,0,0.4)" }}
              >
                <span className="gold-text">${r.amount.toFixed(2)}</span>
                <span className="ash-text mx-1">·</span>
                <span className="text-[#f0e6d2]">{r.product_key || r.kind || "?"}</span>
                <span className="ash-text mx-1">·</span>
                <span className="ash-text">{r.created_at ? new Date(r.created_at).toUTCString().slice(17, 22) : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LedgerCell({ label, primary, sub, testid, primaryColor }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] ash-text">{label}</div>
      <div className="font-heading text-3xl mt-1" style={{ color: primaryColor || "#d4af37" }}>
        {primary}
      </div>
      <div className="text-[10px] tracking-widest ash-text mt-0.5">{sub}</div>
    </div>
  );
}
